import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ArticleItem from './ArticleItem';
import Pagination from '../../common/Pagination';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import articlesService from '../../../services/articles.service';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

function ArticlesList({
    articlesList = [],
    currentPage = 0,
    totalPages = 0,
    totalElements = 0,
    pageSize = 10,
    onPageChange,
    onPageSizeChange,
}) {
    const [showModal, setShowModal] = useState(false);
    const [selectedArticleIds, setSelectedArticleIds] = useState([]);
    const [checkedItems, setCheckedItems] = useState([]);
    const [customMessage, setCustomMessage] = useState('Here is an interesting article for you to read.');

    const todaysAppointment = useEyeconxStore((state) => state.todaysAppointment);
    const user = useEyeconxStore((state) => state.user);
    const [selectedPatient, setSelectedPatient] = useState(null);

    useEffect(() => {
        setCheckedItems(articlesList.map(() => false));
        setSelectedArticleIds([]);
    }, [articlesList]);

    const handleCheckboxChange = (checked, articleId, index) => {
        const updatedChecked = [...checkedItems];
        updatedChecked[index] = checked;
        setCheckedItems(updatedChecked);

        if (checked) {
            setSelectedArticleIds((prev) => [...prev, articleId]);
        } else {
            setSelectedArticleIds((prev) => prev.filter((id) => id !== articleId));
        }
    };

    const handleClearSelected = () => {
        setCheckedItems(articlesList.map(() => false));
        setSelectedArticleIds([]);
    };

    const sendArticleToPatient = () => {
        const data = {
            mediumType: 'EMAIL',
            articleIds: selectedArticleIds,
            patientId: selectedPatient.patientId,
            customMessage,
        };

        articlesService
            .sendArticleToPatient(user.clinicId, data)
            .then(() => {
                toast.success('Articles sent successfully');
                setShowModal(false);
                handleClearSelected();
            })
            .catch(() => {
                toast.error('Error sending articles');
            });
    };

    const startItem = currentPage * pageSize + 1;
    const endItem = Math.min((currentPage + 1) * pageSize, totalElements);

    if (articlesList.length === 0) {
        return (
            <div className="ecx-page-empty flex-grow-1">
                <div className="ecx-page-empty__icon">
                    <i className="bi bi-journal-x"></i>
                </div>
                <h5 className="text-dark fw-normal mb-2">No articles found</h5>
                <p className="mb-0">Try another category, search term, or sort option.</p>
            </div>
        );
    }

    return (
        <>
            {selectedArticleIds.length > 0 && (
                <div className="d-flex justify-content-end mb-3">
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-accent d-flex align-items-center"
                            onClick={() => setShowModal(true)}
                        >
                            <i className="bi bi-send me-1"></i>
                            Send Selected ({selectedArticleIds.length})
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                            onClick={handleClearSelected}
                        >
                            <i className="bi bi-x-circle me-1"></i>
                            Clear
                        </button>
                    </div>
                </div>
            )}

            <div className="d-flex flex-column gap-3 flex-grow-1 ecx-page-scroll">
                {articlesList.map((article, index) => (
                    <ArticleItem
                        key={article.id}
                        article={article}
                        index={index}
                        checked={checkedItems[index]}
                        onCheckboxChange={handleCheckboxChange}
                    />
                ))}
            </div>

            {totalElements > 0 && (
                <div className="ecx-page-pagination border-top pt-3 mt-3">
                    <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                aria-label="Articles per page"
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size} / page
                                    </option>
                                ))}
                            </select>
                            {totalPages <= 1 && (
                                <span className="text-muted small">
                                    Showing {startItem} to {endItem} of {totalElements} entries
                                </span>
                            )}
                        </div>

                        {totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalElements={totalElements}
                                pageSize={pageSize}
                                onPageChange={onPageChange}
                            />
                        )}
                    </div>
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header className="border-0 d-flex align-items-center">
                    <Modal.Title className="flex-grow-1">
                        <i className="bi bi-person-lines-fill me-2"></i>
                        Send Articles to Patient
                    </Modal.Title>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn flex-shrink-0"
                        onClick={() => setShowModal(false)}
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-6 border-end">
                            <h6 className="mb-3 fw-normal">Today&apos;s Appointments</h6>
                            {todaysAppointment?.length > 0 ? (
                                <div className="d-flex flex-wrap gap-2">
                                    {todaysAppointment.map((patient) => (
                                        <button
                                            key={patient.patientId}
                                            type="button"
                                            className={`badge border-0 p-2 ${
                                                selectedPatient?.patientId === patient.patientId
                                                    ? 'bg-primary text-white'
                                                    : 'bg-secondary text-white'
                                            }`}
                                            onClick={() => setSelectedPatient(patient)}
                                        >
                                            {patient.firstName} {patient.lastName}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted mb-0">No patients found</p>
                            )}
                        </div>

                        <div className="col-md-6 mt-4 mt-md-0">
                            <Form.Group>
                                <Form.Label>Custom Message</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={customMessage}
                                    onChange={(e) => setCustomMessage(e.target.value)}
                                />
                            </Form.Group>

                            {selectedPatient && selectedArticleIds.length > 0 && (
                                <div className="mt-3">
                                    <p className="fw-normal mb-3">
                                        Sending {selectedArticleIds.length}{' '}
                                        {selectedArticleIds.length > 1 ? 'articles' : 'article'} to{' '}
                                        <span className="text-primary">
                                            {selectedPatient.firstName} {selectedPatient.lastName}
                                        </span>
                                    </p>
                                    <Button variant="primary" onClick={sendArticleToPatient}>
                                        <i className="bi bi-send me-1"></i>
                                        Send
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ArticlesList;
